/**
 * Image mapping system using ES module imports for production compatibility
 * Maps JSON paths to imported asset URLs that work in both dev and production
 */

// Import all images as ES modules
import centricMxLogo from '@/assets/images/centric-mx-logo.png';
import annualInspectionIcon from '@/assets/images/annual-inspection-icon.png';
import phaseMaintenanceIcon from '@/assets/images/phase-maintenance-icon.png';
import avionicsServicesIcon from '@/assets/images/avionics-services-icon.png';
import helicopterMaintenanceIcon from '@/assets/images/helicopter-maintenance-icon.png';

/**
 * Image mapping from JSON paths to imported asset URLs
 * This ensures proper asset handling in both development and production builds
 */
const imageMap: Record<string, string> = {
  // Centric MX Logo - multiple path mappings for flexibility
  "/wp-content/uploads/2024/10/centric-mx-logo.png": centricMxLogo,
  "/images/centric-mx-logo.png": centricMxLogo,
  "/assets/images/centric-mx-logo.png": centricMxLogo,
  "centric-mx-logo.png": centricMxLogo,
  
  // Service Icons - multiple path mappings for flexibility
  "/wp-content/uploads/2024/10/c38ada61-5332-4334-ae93-c7af4e4ea3ca.png": annualInspectionIcon,
  "/images/annual-inspection-icon.png": annualInspectionIcon,
  "/assets/images/annual-inspection-icon.png": annualInspectionIcon,
  "annual-inspection-icon.png": annualInspectionIcon,
  
  "/wp-content/uploads/2024/10/6b87c998-6fad-4f03-aa51-9413830e4182.png": phaseMaintenanceIcon,
  "/images/phase-maintenance-icon.png": phaseMaintenanceIcon,
  "/assets/images/phase-maintenance-icon.png": phaseMaintenanceIcon,
  "phase-maintenance-icon.png": phaseMaintenanceIcon,
  
  "/wp-content/uploads/2024/10/avionics-icon1.png": avionicsServicesIcon,
  "/images/avionics-services-icon.png": avionicsServicesIcon,
  "/assets/images/avionics-services-icon.png": avionicsServicesIcon,
  "avionics-services-icon.png": avionicsServicesIcon,
  
  "/wp-content/uploads/2024/10/9e267b6d-0142-4754-990c-5cae450903cb.png": helicopterMaintenanceIcon,
  "/images/helicopter-maintenance-icon.png": helicopterMaintenanceIcon,
  "/assets/images/helicopter-maintenance-icon.png": helicopterMaintenanceIcon,
  "helicopter-maintenance-icon.png": helicopterMaintenanceIcon,
};

/**
 * Resolves image paths from JSON data to actual imported asset URLs
 * @param imagePath - Path from JSON data (e.g., "/assets/images/logo.png")
 * @returns Resolved asset URL that works in both dev and production
 */
export function resolveImagePath(imagePath: string): string {
  // First try exact match in image map
  if (imageMap[imagePath]) {
    return imageMap[imagePath];
  }

  // Try normalized paths (with and without /assets prefix)
  const normalizedPath = imagePath.startsWith("/assets/")
    ? imagePath
    : `/assets${imagePath.startsWith("/") ? imagePath : "/" + imagePath}`;

  if (imageMap[normalizedPath]) {
    return imageMap[normalizedPath];
  }

  // Try legacy path format
  const legacyPath = imagePath.replace("/assets", "");
  if (imageMap[legacyPath]) {
    return imageMap[legacyPath];
  }

  // Fallback: log warning and return original path
  console.warn(`Image not found in image map: ${imagePath}. Available images:`, Object.keys(imageMap));
  return imagePath;
}

/**
 * Get all available image paths for debugging
 */
export function getAvailableImages(): string[] {
  return Object.keys(imageMap);
}

/**
 * Legacy export for compatibility
 */
export const resolveImageUrl = resolveImagePath;
